import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { FocusEvent, KeyboardEvent } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { Tooltip } from 'components/ui/Tooltip';
import { TertiaryNavigationAiButton } from './TertiaryNavigation';
import type { TertiaryNavigationAiPromptProps } from './TertiaryNavigation.props';
import {
  TertiaryNavigationAiPromptActions,
  TertiaryNavigationAiPromptBusyHint,
  TertiaryNavigationAiPromptIcon,
  TertiaryNavigationAiPromptInput,
  TertiaryNavigationAiPromptPanel,
  TertiaryNavigationAiPromptRoot,
  TertiaryNavigationSearchSubmitHint,
} from './TertiaryNavigation.styles';
import { TERTIARY_NAVIGATION_TEST_IDS } from './TertiaryNavigation.testIds';
import {
  TERTIARY_NAVIGATION_AI_PROMPT_ARIA_LABEL,
  TERTIARY_NAVIGATION_AI_PROMPT_BUSY_HINT,
  TERTIARY_NAVIGATION_AI_PROMPT_HINT_ACTIVE_MS,
  TERTIARY_NAVIGATION_AI_PROMPT_ICON_SRC,
  TERTIARY_NAVIGATION_AI_PROMPT_MAX_ROWS,
  TERTIARY_NAVIGATION_AI_PROMPT_PANEL_ARIA_LABEL,
  TERTIARY_NAVIGATION_AI_PROMPT_PLACEHOLDER,
  TERTIARY_NAVIGATION_AI_PROMPT_SUBMIT_ARIA_LABEL,
  TERTIARY_NAVIGATION_AI_PROMPT_SUBMIT_HINT,
} from './TertiaryNavigationAiPrompt.constants';

// A collapsed AI trigger in the tertiary bar that opens a floating, auto-growing prompt field.
// Reusable from any page that registers tertiary navigation actions: declare a control config of
// `type: 'aiPrompt'` rather than rendering this directly.
const TertiaryNavigationAiPrompt = ({
  ariaLabel = TERTIARY_NAVIGATION_AI_PROMPT_ARIA_LABEL,
  busyHint = TERTIARY_NAVIGATION_AI_PROMPT_BUSY_HINT,
  collapsedTooltipTitle,
  disabled = false,
  icon,
  isBusy = false,
  maxRows = TERTIARY_NAVIGATION_AI_PROMPT_MAX_ROWS,
  onOpenChange,
  onSubmit,
  panelAriaLabel = TERTIARY_NAVIGATION_AI_PROMPT_PANEL_ARIA_LABEL,
  panelIcon,
  placeholder = TERTIARY_NAVIGATION_AI_PROMPT_PLACEHOLDER,
  submitAriaLabel = TERTIARY_NAVIGATION_AI_PROMPT_SUBMIT_ARIA_LABEL,
  submitHint = TERTIARY_NAVIGATION_AI_PROMPT_SUBMIT_HINT,
  testId,
}: TertiaryNavigationAiPromptProps) => {
  const panelId = useId();
  const [isOpen, setIsOpen] = useState(false);
  // Owned here rather than in the panel, so a stray outside click does not cost the user a prompt
  // they were part way through typing: the panel unmounts, this component does not.
  const [value, setValue] = useState('');
  const [isHintActive, setIsHintActive] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const hintActiveTimeoutRef = useRef<number | null>(null);
  // Mirrors isOpen so the open/close transition can be detected outside a state updater: updaters
  // run twice under Strict Mode, which would fire onOpenChange twice for a single toggle.
  const isOpenRef = useRef(false);

  // TertiaryNavigationAiButton is not a ref-forwarding component, and the tooltip wraps it in an
  // anchor span, so the trigger is reached through the root rather than held directly.
  const focusTrigger = useCallback(() => {
    rootRef.current?.querySelector('button')?.focus();
  }, []);

  // Clear the press-feedback timer if the control unmounts mid-flash.
  useEffect(
    () => () => {
      if (hintActiveTimeoutRef.current !== null) {
        window.clearTimeout(hintActiveTimeoutRef.current);
      }
    },
    []
  );

  const changeOpenState = useCallback(
    (nextIsOpen: boolean) => {
      if (isOpenRef.current === nextIsOpen) {
        return;
      }

      isOpenRef.current = nextIsOpen;
      setIsOpen(nextIsOpen);
      onOpenChange?.(nextIsOpen);
    },
    [onOpenChange]
  );

  const openPanel = useCallback(() => {
    changeOpenState(true);
    // The textarea mounts with the panel, so a synchronous focus would race it.
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, [changeOpenState]);

  const closePanel = useCallback(
    (shouldRestoreFocus: boolean) => {
      changeOpenState(false);
      if (shouldRestoreFocus) {
        focusTrigger();
      }
    },
    [changeOpenState, focusTrigger]
  );

  const handleTriggerClick = useCallback(() => {
    if (isOpen) {
      closePanel(false);
      return;
    }

    openPanel();
  }, [closePanel, isOpen, openPanel]);

  const flashSubmitHint = useCallback(() => {
    if (hintActiveTimeoutRef.current !== null) {
      window.clearTimeout(hintActiveTimeoutRef.current);
    }

    setIsHintActive(true);
    hintActiveTimeoutRef.current = window.setTimeout(() => {
      setIsHintActive(false);
      hintActiveTimeoutRef.current = null;
    }, TERTIARY_NAVIGATION_AI_PROMPT_HINT_ACTIVE_MS);
  }, []);

  const submitPrompt = useCallback(async () => {
    const trimmed = value.trim();
    if (!trimmed || isBusy || disabled) {
      return;
    }

    flashSubmitHint();
    // Only a confirmed success clears the field: a rejected or failed request keeps the prompt so
    // the user can amend it rather than retype it.
    const didSucceed = await onSubmit?.(trimmed);
    if (didSucceed === true) {
      setValue('');
      closePanel(true);
    }
  }, [closePanel, disabled, flashSubmitHint, isBusy, onSubmit, value]);

  const handleKeyDown = useCallback(
    async (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        if (isBusy) {
          return;
        }

        // Stop the key here so an enclosing menu or dialog does not also close.
        event.stopPropagation();
        closePanel(true);
        return;
      }

      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        await submitPrompt();
      }
    },
    [closePanel, isBusy, submitPrompt]
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLSpanElement>) => {
      // Focus moving between the textarea, the hint pill and the trigger stays inside the control.
      if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
        return;
      }

      // Disabling the textarea for a request blurs it in most browsers, so honouring this while
      // busy would collapse the panel the moment a request starts.
      if (isBusy) {
        return;
      }

      closePanel(false);
    },
    [closePanel, isBusy]
  );

  return (
    <TertiaryNavigationAiPromptRoot
      ref={rootRef}
      data-testid={testId ?? TERTIARY_NAVIGATION_TEST_IDS.aiPrompt}
      data-tertiary-navigation-ai-prompt-open={isOpen ? 'true' : undefined}
      onBlur={handleBlur}>
      <TertiaryNavigationAiButton
        aria-controls={isOpen ? panelId : undefined}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        ariaLabel={ariaLabel}
        data-testid={TERTIARY_NAVIGATION_TEST_IDS.aiPromptTrigger}
        disabled={disabled}
        icon={icon}
        onClick={handleTriggerClick}
        tooltipTitle={collapsedTooltipTitle}
      />
      {isOpen && (
        <TertiaryNavigationAiPromptPanel
          aria-busy={isBusy || undefined}
          aria-label={panelAriaLabel}
          data-testid={TERTIARY_NAVIGATION_TEST_IDS.aiPromptPanel}
          id={panelId}
          onKeyDown={handleKeyDown}
          role="dialog">
          <TertiaryNavigationAiPromptIcon aria-hidden="true">
            {panelIcon ?? <img alt="" src={TERTIARY_NAVIGATION_AI_PROMPT_ICON_SRC} />}
          </TertiaryNavigationAiPromptIcon>
          <TertiaryNavigationAiPromptInput
            disabled={isBusy || disabled}
            inputProps={{ 'data-testid': TERTIARY_NAVIGATION_TEST_IDS.aiPromptInput }}
            inputRef={inputRef}
            maxRows={maxRows}
            minRows={1}
            multiline
            onChange={event => setValue(event.target.value)}
            placeholder={placeholder}
            value={value}
          />
          <TertiaryNavigationAiPromptActions>
            {isBusy ? (
              <TertiaryNavigationAiPromptBusyHint aria-live="polite">
                <CircularProgress color="inherit" size={12} thickness={5} />
                {busyHint}
              </TertiaryNavigationAiPromptBusyHint>
            ) : (
              submitHint && (
                <Tooltip placement="bottom" title={submitAriaLabel}>
                  <TertiaryNavigationSearchSubmitHint
                    aria-label={submitAriaLabel}
                    data-active={isHintActive || undefined}
                    data-testid={TERTIARY_NAVIGATION_TEST_IDS.aiPromptSubmitHint}
                    onClick={async () => {
                      await submitPrompt();
                    }}
                    // Without this the press blurs the textarea, which unmounts this button
                    // between mousedown and click and swallows the submit.
                    onMouseDown={event => event.preventDefault()}
                    type="button">
                    {submitHint}
                  </TertiaryNavigationSearchSubmitHint>
                </Tooltip>
              )
            )}
          </TertiaryNavigationAiPromptActions>
        </TertiaryNavigationAiPromptPanel>
      )}
    </TertiaryNavigationAiPromptRoot>
  );
};

export default TertiaryNavigationAiPrompt;
